import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/api';
import {
  FiDownload,
  FiEye,
  FiFileText,
  FiPlus,
  FiSave,
  FiTrash2,
  FiX
} from 'react-icons/fi';
import ZovcurInvoiceHeader, { zovcurLogo } from '../../components/invoices/ZovcurInvoiceHeader';
import { generateZovcurInvoicePdf, getZovcurInvoicePdfBlobUrl } from '../../utils/generateZovcurInvoicePdf';
import {
  createInvoice,
  fetchInvoiceById,
  fetchNextInvoiceNumber,
  purgeLocalInvoiceStorage,
  updateInvoice
} from '../../utils/zovcurInvoiceApi';
import {
  applyInvoiceCompanyDefaults,
  BANK_DEFAULTS,
  buildEmptyInvoice,
  COMPANY_DEFAULTS,
  computeInvoiceTotals,
  computeLine,
  createEmptyProduct,
  findIndianState,
  COMPANY_GSTIN,
  INDIAN_STATES,
  numberToWordsINR,
  formatProductName,
  getProductPricing,
  PRODUCT_OPTIONS
} from '../../utils/zovcurInvoiceStore';

const formatMoney = (value) => (Number(value) || 0).toFixed(2);

/** Convert stock expiry date → invoice Exp format MM/YY (e.g. 04/28) */
const formatExpMmYy = (value) => {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  const mmYy = raw.match(/^(\d{1,2})\s*[\/\-.]\s*(\d{2}|\d{4})$/);
  if (mmYy) {
    const month = String(Math.min(12, Math.max(1, Number(mmYy[1])))).padStart(2, '0');
    const year = mmYy[2].length === 4 ? mmYy[2].slice(-2) : mmYy[2];
    return `${month}/${year}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${month}/${year}`;
};

const getLatestStockBatchExp = (transaction) => {
  const entries = Array.isArray(transaction?.qtyEntries) ? transaction.qtyEntries : [];
  // Prefer newest entry that has batch or expiry
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const row = entries[i];
    const batch = String(row?.batch || '').trim();
    const expiry = row?.expiryDate || null;
    if (batch || expiry) {
      return { batch, exp: formatExpMmYy(expiry) };
    }
  }
  return {
    batch: String(transaction?.batch || '').trim(),
    exp: formatExpMmYy(transaction?.expiryDate)
  };
};

const sectionTitle =
  'text-[11px] font-bold uppercase tracking-widest text-[#1a365d] mb-3 flex items-center gap-2';
const inputCls =
  'flex-1 min-w-0 w-full border-0 border-b border-slate-200 bg-transparent focus:border-teal-500 focus:ring-0 outline-none text-slate-800 py-2 sm:py-1 text-base sm:text-sm';
const readOnlyCls = `${inputCls} bg-slate-100/80 cursor-not-allowed text-slate-600`;
const cardCls = 'bg-white rounded-xl border border-slate-200 p-3 sm:p-4 shadow-sm';
const fieldLabel = 'w-20 sm:w-24 shrink-0 text-xs font-semibold text-slate-500';
const btnSecondary =
  'px-3 sm:px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2 shadow-sm text-sm font-semibold disabled:opacity-60 min-h-[44px]';
const btnTeal =
  'px-3 sm:px-4 py-2.5 rounded-xl border border-teal-600 bg-white text-teal-700 hover:bg-teal-50 flex items-center justify-center gap-2 shadow-sm text-sm font-semibold disabled:opacity-60 min-h-[44px]';
const btnPrimary =
  'px-3 sm:px-4 py-2.5 rounded-xl bg-[#1a365d] text-white hover:bg-[#153050] flex items-center justify-center gap-2 shadow-md text-sm font-semibold disabled:opacity-60 min-h-[44px]';
const detailLabel =
  'w-full sm:w-auto sm:shrink-0 text-[11px] sm:text-xs font-semibold text-slate-500 mb-0.5 sm:mb-0 sm:mr-1';
const detailField =
  'flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-1';

const Invoices = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [invoice, setInvoice] = useState(buildEmptyInvoice());
  const [savedMessage, setSavedMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [salesmen, setSalesmen] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]);
  const [stockByProduct, setStockByProduct] = useState({});

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(API_ENDPOINTS.getProducts, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProductCatalog(response.data || []);
      } catch {
        setProductCatalog([]);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchStockForBatchExp = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(API_ENDPOINTS.getStockTransactions, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = response.data || [];
        const map = {};
        list.forEach((tx) => {
          const key = formatProductName(tx.materialName || tx.product || '');
          if (!key) return;
          const info = getLatestStockBatchExp(tx);
          if (!info.batch && !info.exp) return;
          // Keep first match; prefer entry that has both when replacing empties
          const prev = map[key];
          if (!prev) {
            map[key] = info;
            return;
          }
          map[key] = {
            batch: prev.batch || info.batch,
            exp: prev.exp || info.exp
          };
        });
        setStockByProduct(map);
      } catch {
        setStockByProduct({});
      }
    };
    fetchStockForBatchExp();
  }, []);

  const productOptions = useMemo(() => {
    if (productCatalog.length) {
      return productCatalog.map((p) => p.name).filter(Boolean);
    }
    return PRODUCT_OPTIONS;
  }, [productCatalog]);

  const resolveProductPricing = (name) => {
    const key = formatProductName(name);
    const fromDb = productCatalog.find(
      (item) => formatProductName(item.name) === key
    );
    if (fromDb) {
      return {
        name: fromDb.name,
        mrp: fromDb.mrp,
        rate: fromDb.rate,
        stripSize: fromDb.stripSize,
        stripsPerBox: fromDb.stripsPerBox,
        gstPercent: fromDb.gstPercent,
        batch: fromDb.batch || '',
        exp: fromDb.exp || ''
      };
    }
    return getProductPricing(name);
  };

  const resolveStockBatchExp = (name) => {
    const key = formatProductName(name);
    const fromStock = stockByProduct[key];
    if (fromStock && (fromStock.batch || fromStock.exp)) {
      return {
        batch: fromStock.batch || '',
        exp: fromStock.exp || ''
      };
    }
    const pricing = resolveProductPricing(name);
    return {
      batch: String(pricing?.batch || '').trim(),
      exp: formatExpMmYy(pricing?.exp) || String(pricing?.exp || '').trim()
    };
  };

  useEffect(() => {
    const fetchSalesmen = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(API_ENDPOINTS.getSalesmen, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSalesmen(response.data || []);
      } catch {
        setSalesmen([]);
      }
    };
    fetchSalesmen();
  }, []);

  const salesmanOptions = useMemo(() => {
    const names = salesmen.map((s) => String(s.name || '').trim()).filter(Boolean);
    const current = String(invoice.invoiceDetails?.salesmanName || '').trim();
    if (current && !names.some((n) => n.toLowerCase() === current.toLowerCase())) {
      names.push(current);
    }
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [salesmen, invoice.invoiceDetails?.salesmanName]);

  useEffect(() => {
    let active = true;

    const loadInvoice = async () => {
      setLoading(true);
      try {
        purgeLocalInvoiceStorage();

        if (editId) {
          const existing = await fetchInvoiceById(editId);
          if (active && existing) {
            const number = existing.invoiceNumber || '';
            setInvoice({
              ...existing,
              invoiceDetails: {
                ...existing.invoiceDetails,
                billNo: number
              },
              products: (existing.products || []).map((p) => ({
                ...createEmptyProduct(),
                ...p,
                freeQty: p.freeQty ?? ''
              }))
            });
          }
          return;
        }

        const invoiceNumber = await fetchNextInvoiceNumber();
        if (active) setInvoice(buildEmptyInvoice(invoiceNumber));
      } catch {
        if (active) {
          alert('Failed to load invoice data. Please check your login and try again.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadInvoice();
    return () => {
      active = false;
    };
  }, [editId]);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  const totals = useMemo(() => computeInvoiceTotals(invoice.products), [invoice.products]);
  const amountInWords = useMemo(
    () => numberToWordsINR(totals.invoiceAmount),
    [totals.invoiceAmount]
  );

  const updateField = (section, field, value) => {
    setInvoice((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const updateInvoiceDetail = (field, value) => {
    setInvoice((prev) => ({
      ...prev,
      invoiceDetails: { ...prev.invoiceDetails, [field]: value }
    }));
  };

  /** Invoice # and Bill No stay the same; both can be typed manually */
  const updateBillNumber = (value) => {
    setInvoice((prev) => ({
      ...prev,
      invoiceNumber: value,
      invoiceDetails: { ...prev.invoiceDetails, billNo: value }
    }));
  };

  const handleCustomerStateChange = (stateName) => {
    const match = findIndianState(stateName);
    setInvoice((prev) => ({
      ...prev,
      customer: { ...prev.customer, state: stateName },
      invoiceDetails: {
        ...prev.invoiceDetails,
        stateName: match?.name || stateName,
        stateCode: match?.code || prev.invoiceDetails.stateCode
      }
    }));
  };

  const updateProduct = (id, field, value) => {
    setInvoice((prev) => ({
      ...prev,
      products: prev.products.map((p) => {
        if (p.id !== id) return p;
        if (field === 'product') {
          const product = formatProductName(value);
          const pricing = resolveProductPricing(product);
          const stockInfo = resolveStockBatchExp(product);
          return {
            ...p,
            product,
            batchNo: stockInfo.batch || '',
            exp: stockInfo.exp || '',
            ...(pricing
              ? {
                  rate: String(pricing.rate),
                  mrp: String(pricing.mrp),
                  gstPercent: String(pricing.gstPercent ?? p.gstPercent ?? '5')
                }
              : {})
          };
        }
        return { ...p, [field]: value };
      })
    }));
  };

  const addProduct = () => {
    setInvoice((prev) => ({
      ...prev,
      products: [...prev.products, createEmptyProduct()]
    }));
  };

  const removeProduct = (id) => {
    setInvoice((prev) => ({
      ...prev,
      products: prev.products.length > 1 ? prev.products.filter((p) => p.id !== id) : prev.products
    }));
  };

  const handleSave = async () => {
    const billNumber = String(
      invoice.invoiceNumber || invoice.invoiceDetails?.billNo || ''
    ).trim();
    if (!billNumber) {
      alert('Please enter Bill No before saving.');
      return;
    }

    setSaving(true);
    try {
      const isNew = !invoice.id;
      const invoiceNumber = billNumber;

      const payload = applyInvoiceCompanyDefaults({
        ...invoice,
        invoiceNumber,
        products: invoice.products.map((p) => ({
          ...p,
          product: formatProductName(p.product)
        })),
        invoiceDetails: {
          ...invoice.invoiceDetails,
          billNo: invoiceNumber,
          gstin: COMPANY_GSTIN
        }
      });

      const { id, createdAt, ...body } = payload;
      const productsToSave = payload.products.map((p) => ({
        id: p.id,
        product: p.product,
        batchNo: p.batchNo || '',
        exp: p.exp || '',
        qty: p.qty === '' || p.qty == null ? '' : String(p.qty),
        freeQty: p.freeQty === '' || p.freeQty == null ? '' : String(p.freeQty),
        rate: p.rate === '' || p.rate == null ? '' : String(p.rate),
        mrp: p.mrp === '' || p.mrp == null ? '' : String(p.mrp),
        gstPercent: p.gstPercent == null ? '5' : String(p.gstPercent)
      }));

      const saved = isNew
        ? await createInvoice({ ...body, invoiceNumber, products: productsToSave })
        : await updateInvoice(invoice.id, {
            ...body,
            invoiceNumber,
            products: productsToSave
          });

      // Keep freeQty even if an older API response omits it
      const mergedProducts = (saved.products || productsToSave).map((p, i) => ({
        ...createEmptyProduct(),
        ...productsToSave[i],
        ...p,
        freeQty: p.freeQty ?? productsToSave[i]?.freeQty ?? ''
      }));

      setInvoice({
        ...saved,
        invoiceNumber: saved.invoiceNumber || invoiceNumber,
        invoiceDetails: {
          ...saved.invoiceDetails,
          billNo: saved.invoiceNumber || invoiceNumber
        },
        products: mergedProducts
      });
      setSavedMessage(
        `Invoice ${saved.invoiceNumber || invoiceNumber} saved to database.`
      );
      setTimeout(() => setSavedMessage(''), 3000);

      if (isNew && saved.id) {
        navigate(`/invoices?edit=${saved.id}`, { replace: true });
      }
    } catch {
      alert('Failed to save invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      await generateZovcurInvoicePdf(applyInvoiceCompanyDefaults(invoice), zovcurLogo);
    } catch {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePreviewPdf = async () => {
    setPdfLoading(true);
    try {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      const url = await getZovcurInvoicePdfBlobUrl(applyInvoiceCompanyDefaults(invoice), zovcurLogo);
      setPdfPreviewUrl(url);
      setShowPdfPreview(true);
    } catch {
      alert('Failed to generate PDF preview. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const gstRows = Object.entries(totals.gstBuckets).sort((a, b) => Number(a[0]) - Number(b[0]));
  const productInputCls = 'w-full px-1 py-0.5 outline-none bg-transparent text-[11px]';
  const productSelectCls = `${productInputCls} min-w-[90px] font-bold uppercase`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f7fa] p-4 md:p-6 flex items-center justify-center font-[Montserrat,sans-serif]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-9 w-9 border-b-2 border-[#1a365d] mb-3" />
          <p className="text-slate-500 text-sm">Loading invoice…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f7fa] p-3 sm:p-4 md:p-6 pb-28 md:pb-6 font-[Montserrat,sans-serif]">
      <div className="max-w-6xl mx-auto mb-4 sm:mb-5 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-start justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-2">
            <span className="inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-[#1a365d] text-white shrink-0">
              <FiFileText />
            </span>
            <span className="truncate">Tax Invoice</span>
          </h1>
          <p className="text-slate-500 mt-1 text-xs sm:text-sm">
            {invoice.id
              ? `Editing ${invoice.invoiceNumber}`
              : 'Create a new Zovcur Pharma tax invoice'}
          </p>
        </div>
        <div className="hidden md:flex flex-wrap gap-2">
          <button type="button" onClick={() => navigate('/orders')} className={btnSecondary}>
            View Orders
          </button>
          <button
            type="button"
            onClick={handlePreviewPdf}
            disabled={pdfLoading}
            className={btnSecondary}
          >
            <FiEye /> {pdfLoading ? 'Generating…' : 'Preview PDF'}
          </button>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            className={btnTeal}
          >
            <FiDownload /> {pdfLoading ? 'Generating…' : 'Download PDF'}
          </button>
          <button type="button" onClick={handleSave} disabled={saving} className={btnPrimary}>
            <FiSave /> {saving ? 'Saving…' : 'Save Invoice'}
          </button>
        </div>
      </div>

      {savedMessage && (
        <div className="max-w-6xl mx-auto mb-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5 text-sm font-medium">
          {savedMessage}
        </div>
      )}

      <div
        id="zovcur-invoice-document"
        className="max-w-6xl mx-auto bg-white border border-slate-200 shadow-sm rounded-2xl overflow-hidden text-sm text-slate-800"
      >
        <ZovcurInvoiceHeader
          company={{ ...invoice.company, dlNo: COMPANY_DEFAULTS.dlNo }}
          invoiceDetails={invoice.invoiceDetails}
          invoiceNumber={invoice.invoiceNumber}
        />

        <div className="grid md:grid-cols-2 gap-4 p-4 md:p-5 border-b border-slate-200 bg-[#f8fafb]">
          <div className={cardCls}>
            <h3 className={sectionTitle}>
              <span className="w-1 h-4 bg-teal-500 rounded-full" />
              Customer Details
            </h3>
            {[
              ['doctor', 'Doctor'],
              ['address', 'Address'],
              ['phone', 'Phone'],
              ['gstin', 'GSTIN'],
              ['dlNo', 'DL No']
            ].map(([key, label]) => (
              <div key={key} className="flex flex-col sm:flex-row gap-1 sm:gap-2 mb-2.5 sm:items-center">
                <span className={fieldLabel}>{label}:</span>
                <input
                  className={inputCls}
                  value={invoice.customer[key] || ''}
                  onChange={(e) => updateField('customer', key, e.target.value)}
                  placeholder={key === 'gstin' ? 'Customer GSTIN' : key === 'dlNo' ? 'Customer DL No' : undefined}
                />
              </div>
            ))}
            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 mb-1 sm:items-center">
              <span className={fieldLabel}>State:</span>
              <select
                className={`${inputCls} bg-transparent`}
                value={invoice.customer.state || invoice.invoiceDetails.stateName || ''}
                onChange={(e) => handleCustomerStateChange(e.target.value)}
              >
                <option value="">Select state</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s.code} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={cardCls}>
            <h3 className={sectionTitle}>
              <span className="w-1 h-4 bg-teal-500 rounded-full" />
              Invoice Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3 sm:gap-y-2.5">
              <label className={`${detailField} sm:col-span-2`}>
                <span className={`${detailLabel} sm:w-28`}>Bill Type:</span>
                <input
                  className={inputCls}
                  value={invoice.invoiceDetails.billType}
                  onChange={(e) => updateInvoiceDetail('billType', e.target.value)}
                />
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-20`}>Bill No:</span>
                <input
                  className={`${inputCls} font-bold text-[#1a365d]`}
                  value={invoice.invoiceNumber || invoice.invoiceDetails.billNo || ''}
                  onChange={(e) => updateBillNumber(e.target.value)}
                  placeholder="e.g. Zov009"
                  title="Invoice and Bill number (same)"
                />
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-20`}>Page No:</span>
                <input
                  className={inputCls}
                  value={invoice.invoiceDetails.pageNo}
                  onChange={(e) => updateInvoiceDetail('pageNo', e.target.value)}
                />
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-20`}>Bill Date:</span>
                <input
                  type="date"
                  className={inputCls}
                  value={invoice.invoiceDetails.billDate}
                  onChange={(e) => updateInvoiceDetail('billDate', e.target.value)}
                />
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-20`}>Order Date:</span>
                <input
                  type="date"
                  className={inputCls}
                  value={invoice.invoiceDetails.orderDate}
                  onChange={(e) => updateInvoiceDetail('orderDate', e.target.value)}
                />
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-20`}>Due Date:</span>
                <input
                  type="date"
                  className={inputCls}
                  value={invoice.invoiceDetails.dueDate || ''}
                  onChange={(e) => updateInvoiceDetail('dueDate', e.target.value)}
                />
              </label>
              <label className={`${detailField} sm:col-span-2`}>
                <span className={`${detailLabel} sm:w-28`}>Salesman Name:</span>
                <select
                  className={`${inputCls} bg-transparent`}
                  value={invoice.invoiceDetails.salesmanName || ''}
                  onChange={(e) => updateInvoiceDetail('salesmanName', e.target.value)}
                >
                  <option value="">Select salesman</option>
                  {salesmanOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {salesmanOptions.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => navigate('/salesman')}
                    className="text-[11px] text-teal-700 font-semibold mt-1 hover:underline text-left"
                  >
                    Add salesmen in Salesman page
                  </button>
                ) : null}
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-24`}>State Code:</span>
                <input
                  className={readOnlyCls}
                  value={invoice.invoiceDetails.stateCode}
                  readOnly
                />
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-24`}>State Name:</span>
                <input
                  className={readOnlyCls}
                  value={invoice.invoiceDetails.stateName}
                  readOnly
                />
              </label>
              <label className={`${detailField} sm:col-span-2`}>
                <span className={`${detailLabel} sm:w-20`}>GSTIN:</span>
                <input className={readOnlyCls} value={COMPANY_GSTIN} readOnly />
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-20`}>REG No:</span>
                <input
                  className={inputCls}
                  value={invoice.invoiceDetails.regNo}
                  onChange={(e) => updateInvoiceDetail('regNo', e.target.value)}
                />
              </label>
              <label className={detailField}>
                <span className={`${detailLabel} sm:w-24`}>Delivery Type:</span>
                <input
                  className={inputCls}
                  value={invoice.invoiceDetails.deliveryType}
                  onChange={(e) => updateInvoiceDetail('deliveryType', e.target.value)}
                />
              </label>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <label className="flex gap-2 text-xs items-center">
                <span className="text-slate-500 font-semibold">DL No (Company):</span>
                <input className={readOnlyCls} value={COMPANY_DEFAULTS.dlNo} readOnly />
              </label>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-5 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className={sectionTitle + ' mb-0'}>
              <span className="w-1 h-4 bg-teal-500 rounded-full" />
              Products
            </h3>
            <button
              type="button"
              onClick={addProduct}
              className="text-teal-700 hover:text-teal-900 flex items-center gap-1.5 text-xs font-bold bg-teal-50 hover:bg-teal-100 px-3 py-2 rounded-xl border border-teal-100"
            >
              <FiPlus /> Add Row
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">
            Select a product to auto-fill rate, MRP, batch &amp; exp (MM/YY) from Stock In/Out.
            Qty is in strips. Free Qty uses the same rate for cost tracking but is not billed.
          </p>

          {/* Mobile product cards */}
          <div className="md:hidden space-y-3">
            {invoice.products.map((row, index) => {
              const line = computeLine(row);
              return (
                <div
                  key={row.id}
                  className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-lg bg-[#1a365d] px-2 text-xs font-bold text-white">
                      {index + 1}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#1a365d]">
                        ₹{formatMoney(line.total)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeProduct(row.id)}
                        className="text-rose-500 hover:bg-rose-50 p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label="Remove row"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <label className="block mb-2.5">
                    <span className="text-[11px] font-semibold text-slate-500">Product</span>
                    <select
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-bold uppercase outline-none focus:border-teal-500"
                      value={row.product}
                      onChange={(e) => updateProduct(row.id, 'product', e.target.value)}
                    >
                      <option value="">Select product</option>
                      {row.product &&
                      !productOptions.some(
                        (name) => formatProductName(name) === formatProductName(row.product)
                      ) ? (
                        <option value={row.product}>{formatProductName(row.product)}</option>
                      ) : null}
                      {productOptions.map((name) => (
                        <option key={name} value={formatProductName(name)}>
                          {formatProductName(name)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="grid grid-cols-2 gap-2.5">
                    <label className="block">
                      <span className="text-[11px] font-semibold text-slate-500">Batch</span>
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                        value={row.batchNo}
                        onChange={(e) => updateProduct(row.id, 'batchNo', e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-slate-500">Exp</span>
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                        placeholder="MM/YY"
                        value={row.exp}
                        onChange={(e) => updateProduct(row.id, 'exp', e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-slate-500">Qty (strips)</span>
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                        value={row.qty}
                        onChange={(e) => updateProduct(row.id, 'qty', e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-amber-800">Free (unbilled)</span>
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        className="mt-1 w-full rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2.5 text-sm outline-none focus:border-amber-500"
                        value={row.freeQty ?? ''}
                        onChange={(e) => updateProduct(row.id, 'freeQty', e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-slate-500">Rate</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                        value={row.rate}
                        onChange={(e) => updateProduct(row.id, 'rate', e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-slate-500">MRP</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                        value={row.mrp}
                        onChange={(e) => updateProduct(row.id, 'mrp', e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-slate-500">GST %</span>
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                        value={row.gstPercent}
                        onChange={(e) => updateProduct(row.id, 'gstPercent', e.target.value)}
                      />
                    </label>
                    <div className="flex flex-col justify-end">
                      <span className="text-[11px] font-semibold text-slate-500">Line total</span>
                      <p className="mt-1 rounded-lg bg-[#1a365d]/5 px-3 py-2.5 text-sm font-bold text-[#1a365d]">
                        ₹{formatMoney(line.total)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop products table */}
          <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
            <table className="w-full border-collapse text-xs min-w-[720px]">
              <thead>
                <tr className="bg-[#1a365d] text-white">
                  <th className="px-2 py-2.5 w-8 text-left font-semibold uppercase tracking-wide">
                    S.No
                  </th>
                  <th className="px-2 py-2.5 min-w-[110px] text-left font-semibold uppercase tracking-wide">
                    Product
                  </th>
                  <th className="px-2 py-2.5 w-16 font-semibold uppercase tracking-wide">Batch</th>
                  <th className="px-2 py-2.5 w-12 font-semibold uppercase tracking-wide">Exp</th>
                  <th
                    className="px-2 py-2.5 w-10 font-semibold uppercase tracking-wide"
                    title="Billed quantity in strips"
                  >
                    Qty
                  </th>
                  <th
                    className="px-2 py-2.5 w-10 font-semibold uppercase tracking-wide"
                    title="Free quantity in strips (cost tracked, not billed)"
                  >
                    Free
                  </th>
                  <th
                    className="px-2 py-2.5 w-12 font-semibold uppercase tracking-wide"
                    title="Rate per strip (10 tabs)"
                  >
                    Rate
                  </th>
                  <th
                    className="px-2 py-2.5 w-12 font-semibold uppercase tracking-wide"
                    title="MRP per strip (10 tabs)"
                  >
                    MRP
                  </th>
                  <th className="px-2 py-2.5 w-10 font-semibold uppercase tracking-wide">GST</th>
                  <th className="px-2 py-2.5 w-14 text-right font-semibold uppercase tracking-wide">
                    Total
                  </th>
                  <th className="px-2 py-2.5 w-8" />
                </tr>
              </thead>
              <tbody>
                {invoice.products.map((row, index) => {
                  const line = computeLine(row);
                  return (
                    <tr
                      key={row.id}
                      className={`border-b border-slate-100 hover:bg-teal-50/30 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
                      }`}
                    >
                      <td className="px-2 py-1.5 text-center font-semibold text-slate-600">
                        {index + 1}
                      </td>
                      <td className="p-0.5">
                        <select
                          className={productSelectCls}
                          value={row.product}
                          onChange={(e) => updateProduct(row.id, 'product', e.target.value)}
                        >
                          <option value="">Select product</option>
                          {row.product &&
                          !productOptions.some(
                            (name) => formatProductName(name) === formatProductName(row.product)
                          ) ? (
                            <option value={row.product}>{formatProductName(row.product)}</option>
                          ) : null}
                          {productOptions.map((name) => (
                            <option key={name} value={formatProductName(name)}>
                              {formatProductName(name)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="p-0.5">
                        <input
                          className={productInputCls}
                          value={row.batchNo}
                          onChange={(e) => updateProduct(row.id, 'batchNo', e.target.value)}
                        />
                      </td>
                      <td className="p-0.5">
                        <input
                          className={productInputCls}
                          placeholder="MM/YY"
                          value={row.exp}
                          onChange={(e) => updateProduct(row.id, 'exp', e.target.value)}
                        />
                      </td>
                      <td className="p-0.5">
                        <input
                          type="number"
                          min="0"
                          className={`${productInputCls} text-center`}
                          value={row.qty}
                          onChange={(e) => updateProduct(row.id, 'qty', e.target.value)}
                          title="Billed strips"
                        />
                      </td>
                      <td className="p-0.5">
                        <input
                          type="number"
                          min="0"
                          className={`${productInputCls} text-center`}
                          value={row.freeQty ?? ''}
                          onChange={(e) => updateProduct(row.id, 'freeQty', e.target.value)}
                          title="Free strips (not billed)"
                        />
                      </td>
                      <td className="p-0.5">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={`${productInputCls} text-right`}
                          value={row.rate}
                          onChange={(e) => updateProduct(row.id, 'rate', e.target.value)}
                          title="Rate per strip (10 tablets)"
                        />
                      </td>
                      <td className="p-0.5">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={`${productInputCls} text-right`}
                          value={row.mrp}
                          onChange={(e) => updateProduct(row.id, 'mrp', e.target.value)}
                          title="MRP per strip (10 tablets)"
                        />
                      </td>
                      <td className="p-0.5">
                        <input
                          className={`${productInputCls} text-center`}
                          value={row.gstPercent}
                          onChange={(e) => updateProduct(row.id, 'gstPercent', e.target.value)}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right font-bold text-[#1a365d] text-[11px]">
                        {formatMoney(line.total)}
                      </td>
                      <td className="p-1 text-center">
                        <button
                          type="button"
                          onClick={() => removeProduct(row.id)}
                          className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded-lg"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 p-4 md:p-5 border-b border-slate-200 bg-[#f8fafb]">
          <div className={cardCls}>
            <h3 className={sectionTitle}>
              <span className="w-1 h-4 bg-teal-500 rounded-full" />
              Summary
            </h3>
            <div className="space-y-2">
              {[
                ['Items', totals.items],
                ['Quantity (strips)', totals.quantity],
                ['Free Qty (strips)', totals.freeQuantity || 0],
                ['Free Cost (not billed)', formatMoney(totals.freeCost || 0)],
                ['Base Amount', formatMoney(totals.baseAmount)],
                ['SGST', formatMoney(totals.sgst)],
                ['CGST', formatMoney(totals.cgst)],
                ['GST Total', formatMoney(totals.gstTotal)]
              ].map(([label, val]) => (
                <div
                  key={label}
                  className={`flex justify-between text-sm ${
                    label.startsWith('Free') ? 'text-amber-800' : 'text-slate-600'
                  }`}
                >
                  <span>{label}</span>
                  <span className="font-semibold text-slate-800">{val}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-base border-t border-teal-200 pt-2.5 mt-2 text-[#1a365d]">
                <span>Invoice Amount</span>
                <span>₹{formatMoney(totals.invoiceAmount)}</span>
              </div>
            </div>
          </div>

          <div className={cardCls}>
            <h3 className={sectionTitle}>
              <span className="w-1 h-4 bg-teal-500 rounded-full" />
              GST Summary
            </h3>
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-[#1a365d] text-white">
                    <th className="p-2 text-left font-semibold">Category</th>
                    <th className="p-2 text-right font-semibold">Base</th>
                    <th className="p-2 text-right font-semibold">GST</th>
                    <th className="p-2 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {gstRows.length === 0 ? (
                    <tr>
                      <td className="p-2 text-slate-400" colSpan={4}>
                        —
                      </td>
                    </tr>
                  ) : (
                    gstRows.map(([pct, bucket]) => (
                      <tr key={pct} className="border-t border-slate-100 bg-white">
                        <td className="p-2">GST {pct}%</td>
                        <td className="p-2 text-right">{formatMoney(bucket.base)}</td>
                        <td className="p-2 text-right">{formatMoney(bucket.gst)}</td>
                        <td className="p-2 text-right font-semibold">
                          {formatMoney(bucket.amount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50/40">
          <p className="text-[11px] text-slate-500 mb-1 font-bold uppercase tracking-wide">
            Amount in Words
          </p>
          <p className="font-semibold italic text-[#1a365d] text-sm md:text-base">
            {amountInWords}
          </p>
        </div>

        <div className="p-4 md:p-5">
          <h3 className={sectionTitle}>
            <span className="w-1 h-4 bg-teal-500 rounded-full" />
            Bank Details
          </h3>
          <div className="grid md:grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
            {[
              ['bank', 'Bank'],
              ['acNo', 'A/C No'],
              ['ifsc', 'IFSC'],
              ['branch', 'Branch']
            ].map(([key, label]) => (
              <label key={key} className="flex gap-2 items-center">
                <span className="w-20 shrink-0 text-xs font-semibold text-slate-500">{label}:</span>
                <input className={readOnlyCls} value={BANK_DEFAULTS[key]} readOnly />
              </label>
            ))}
          </div>
        </div>

        <div className="bg-[#1a365d] px-4 py-2.5 text-center text-[11px] text-teal-100">
          Zovcur Pharma — Computer Generated Tax Invoice
        </div>
      </div>

      <div className="hidden md:flex max-w-6xl mx-auto mt-4 flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={handlePreviewPdf}
          disabled={pdfLoading}
          className={btnSecondary}
        >
          <FiEye /> Preview PDF
        </button>
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={pdfLoading}
          className="px-4 py-2.5 rounded-xl bg-teal-600 text-white hover:bg-teal-700 flex items-center gap-2 shadow-sm text-sm font-semibold disabled:opacity-60 min-h-[44px]"
        >
          <FiDownload /> Download PDF
        </button>
        <button
          type="button"
          onClick={() => {
            setInvoice(buildEmptyInvoice());
            navigate('/invoices');
          }}
          className={btnSecondary}
        >
          <FiX /> New Invoice
        </button>
        <button type="button" onClick={handleSave} disabled={saving} className={btnPrimary}>
          <FiSave /> {saving ? 'Saving…' : 'Save Invoice'}
        </button>
      </div>

      {/* Mobile sticky billing actions */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-sm shadow-[0_-8px_24px_rgba(15,23,42,0.08)] pb-[env(safe-area-inset-bottom)]">
        <div className="px-3 pt-2 pb-2 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
              Invoice amount
            </p>
            <p className="text-base font-bold text-[#1a365d] truncate">
              ₹{formatMoney(totals.invoiceAmount)}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={handlePreviewPdf}
              disabled={pdfLoading}
              className="p-2.5 rounded-xl border border-slate-200 text-slate-700 disabled:opacity-60"
              aria-label="Preview PDF"
            >
              <FiEye size={18} />
            </button>
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="p-2.5 rounded-xl border border-teal-600 text-teal-700 disabled:opacity-60"
              aria-label="Download PDF"
            >
              <FiDownload size={18} />
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#1a365d] text-white text-sm font-semibold disabled:opacity-60"
            >
              <FiSave size={16} /> {saving ? '…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {showPdfPreview && pdfPreviewUrl && (
        <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50">
          <div className="bg-white sm:rounded-2xl shadow-2xl w-full sm:max-w-5xl h-full sm:h-[90vh] flex flex-col overflow-hidden">
            <div className="px-3 sm:px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0 bg-[#1a365d] text-white gap-2">
              <h2 className="text-sm sm:text-lg font-bold truncate">
                PDF — {invoice.invoiceDetails?.billNo || invoice.invoiceNumber || 'Draft'}
              </h2>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  className="px-3 py-1.5 rounded-xl bg-teal-500 text-white hover:bg-teal-400 flex items-center gap-2 text-sm font-semibold disabled:opacity-60"
                >
                  <FiDownload /> <span className="hidden sm:inline">Download</span>
                </button>
                <button
                  type="button"
                  onClick={closePdfPreview}
                  className="p-2 rounded-lg text-white/80 hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Close preview"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <iframe
              title="Invoice PDF preview"
              src={pdfPreviewUrl}
              className="flex-1 w-full border-0 bg-slate-100"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
